/**
 * GA4 ecommerce / funnel helpers for PromoTix secondary (TN) marketplace.
 *
 * Uses the existing Sharetribe-injected window.gtag — never initializes GA4.
 * Purchase payload builders must stay aligned with
 * `server/api-util/ga4SecondaryPurchasePayload.js` (Measurement Protocol).
 *
 * @see src/util/includeScripts.js
 * @see server/api-util/ga4MeasurementProtocol.js
 */

import Decimal from 'decimal.js';

import appSettings from '../config/settings';
import { isTicketNetworkEventListing } from './listingEventDate';
import { getProcess, resolveLatestProcessName } from '../transactions/transaction';
import {
  states as purchaseStates,
  transitions as purchaseTransitions,
} from '../transactions/transactionProcessPurchase';

// Inline codes to avoid importing util/types (pulls sdkLoader / PropTypes).
const LINE_ITEM_ITEM = 'line-item/item';
const LINE_ITEM_CUSTOMER_COMMISSION = 'line-item/customer-commission';
const LINE_ITEM_PROVIDER_COMMISSION = 'line-item/provider-commission';
const LINE_ITEM_TEAK_PROTECTION = 'line-item/teak-protection';

const { subUnitDivisors } = appSettings;

export const GA4_AFFILIATION = 'PromoTix Secondary Marketplace';
export const GA4_ITEM_CATEGORY_TICKETS = 'Secondary Tickets';
export const GA4_ITEM_CATEGORY_PROTECTION = 'Ticket Protection';
export const GA4_PROTECTION_ITEM_ID = 'ticket_protection';
export const GA4_SECONDARY_ITEM_LIST_ID = 'secondary_ticket_inventory';
export const GA4_SECONDARY_ITEM_LIST_NAME = 'Secondary Ticket Inventory';

const PURCHASE_DEDUP_PREFIX = 'ga4_secondary_purchase:';
const REFUND_DEDUP_PREFIX = 'ga4_secondary_refund:';
const BEGIN_CHECKOUT_DEDUP_PREFIX = 'ga4_secondary_begin_checkout:';
const INVENTORY_DEDUP_PREFIX = 'ga4_secondary_inventory:';
const VIEW_ITEM_DEDUP_PREFIX = 'ga4_secondary_view_item:';
const GA4_IDS_SESSION_KEY = 'ga4_secondary_attribution_ids';

/** localStorage TTL for purchase/refund dedupe (90 days). */
export const GA4_DEDUP_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const POST_CAPTURE_PURCHASE_STATES = new Set([
  purchaseStates.PURCHASED,
  purchaseStates.DELIVERED,
  purchaseStates.RECEIVED,
  purchaseStates.COMPLETED,
  purchaseStates.DISPUTED,
  purchaseStates.REVIEWED_BY_CUSTOMER,
  purchaseStates.REVIEWED_BY_PROVIDER,
  purchaseStates.REVIEWED,
]);

const CAPTURE_TRANSITIONS = new Set([
  purchaseTransitions.ACCEPT,
  purchaseTransitions.OPERATOR_ACCEPT,
  // Fully rewards-covered checkout lands in purchased without Stripe accept.
  purchaseTransitions.REQUEST_FREE_PURCHASE,
  purchaseTransitions.REQUEST_FREE_PURCHASE_AFTER_INQUIRY,
]);

const PAID_STRIPE_REFUND_TRANSITIONS = new Set([purchaseTransitions.CANCEL]);

// —— storage ——

const safeSessionGet = key => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

const safeLocalGet = key => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeLocalSet = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

export const hasLocalDedupe = (prefix, id, now = Date.now()) => {
  if (!id) return false;
  const raw = safeLocalGet(`${prefix}${id}`);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return true;
  if (now - ts > GA4_DEDUP_TTL_MS) return false;
  return true;
};

export const markLocalDedupe = (prefix, id, now = Date.now()) => {
  if (!id) return;
  safeLocalSet(`${prefix}${id}`, String(now));
};

// —— primitives ——

export const isGa4Available = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

const PII_KEY_RE = /^(email|e_?mail|phone|telephone|address|postal|zip|ip|ip_address|customer_name|first_name|last_name|full_name|name)$/i;

export const sanitizeGa4Params = (params = {}) => {
  const out = {};
  Object.keys(params || {}).forEach(key => {
    if (PII_KEY_RE.test(key)) return;
    const v = params[key];
    if (v === undefined || v === null || v === '') return;
    out[key] = v;
  });
  return out;
};

export const trackGa4Event = (eventName, params = {}) => {
  if (!eventName || !isGa4Available()) return false;
  try {
    window.gtag('event', eventName, sanitizeGa4Params(params));
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ga4-secondary] track failed', eventName, e);
    return false;
  }
};

/**
 * Resolve configured GA4 measurement ID (never hardcode). Prefers Console/config exposure.
 */
export const resolveGa4MeasurementId = (explicit) => {
  const fromArg = explicit != null ? String(explicit).trim() : '';
  if (fromArg.indexOf('G-') === 0) return fromArg;
  if (typeof window !== 'undefined') {
    const fromWindow = String(
      window.__PROMOTIX_GA4_MEASUREMENT_ID__ || window.__PROMTIX_GA4_MEASUREMENT_ID__ || ''
    ).trim();
    if (fromWindow.indexOf('G-') === 0) return fromWindow;
  }
  const fromEnv = String(process.env.REACT_APP_GOOGLE_ANALYTICS_ID || '').trim();
  if (fromEnv.indexOf('G-') === 0) return fromEnv;
  return '';
};

const gtagGetField = (measurementId, fieldName) =>
  new Promise(resolve => {
    if (!isGa4Available() || !measurementId) {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = value => {
      if (settled) return;
      settled = true;
      resolve(value != null && value !== '' ? String(value) : null);
    };
    try {
      window.gtag('get', measurementId, fieldName, finish);
      setTimeout(() => finish(null), 1500);
    } catch (e) {
      finish(null);
    }
  });

/**
 * Read client_id + session_id from the existing gtag instance.
 * No-ops when gtag/measurement ID unavailable (consent / not loaded).
 */
export const getGa4ClientAndSessionIds = async (measurementId) => {
  const id = resolveGa4MeasurementId(measurementId);
  if (!id || !isGa4Available()) {
    return { clientId: null, sessionId: null, measurementId: id || null };
  }
  const [clientId, sessionId] = await Promise.all([
    gtagGetField(id, 'client_id'),
    gtagGetField(id, 'session_id'),
  ]);
  return { clientId, sessionId, measurementId: id };
};

export const cacheGa4AttributionIds = ids => {
  if (!ids?.clientId && !ids?.sessionId) return;
  try {
    safeSessionSet(
      GA4_IDS_SESSION_KEY,
      JSON.stringify({
        clientId: ids.clientId || null,
        sessionId: ids.sessionId || null,
        measurementId: ids.measurementId || null,
        capturedAt: new Date().toISOString(),
      })
    );
  } catch (e) {
    // ignore
  }
};

export const readCachedGa4AttributionIds = () => {
  const raw = safeSessionGet(GA4_IDS_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * Persist GA4 attribution on transaction metadata (server Integration API).
 * Fire-and-forget safe; never throws to callers.
 */
export const persistGa4AttributionForTransaction = async (transactionId, measurementId) => {
  const txId =
    typeof transactionId === 'string'
      ? transactionId
      : transactionId?.uuid || transactionId?.id?.uuid || null;
  if (!txId) return { ok: false, reason: 'no_transaction_id' };

  let ids = readCachedGa4AttributionIds();
  if (!ids?.clientId) {
    ids = await getGa4ClientAndSessionIds(measurementId);
    cacheGa4AttributionIds(ids);
  }
  if (!ids?.clientId) {
    return { ok: false, reason: 'no_client_id' };
  }

  try {
    // Lazy import keeps unit tests free of sdkLoader/axios via api.js
    const { recordGa4AttributionMetadata } = await import('./api');
    await recordGa4AttributionMetadata({
      transactionId: txId,
      clientId: ids.clientId,
      sessionId: ids.sessionId || undefined,
      measurementId: ids.measurementId || resolveGa4MeasurementId(measurementId) || undefined,
      capturedAt: ids.capturedAt || new Date().toISOString(),
    });
    return { ok: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ga4-secondary] attribution persist failed', e);
    return { ok: false, reason: 'api_error' };
  }
};

export const moneyToMajor = moneyLike => {
  if (!moneyLike || moneyLike.amount == null) return null;
  const currency = moneyLike.currency || 'USD';
  try {
    const amount = Number(
      moneyLike.amount && typeof moneyLike.amount.toString === 'function'
        ? moneyLike.amount.toString()
        : moneyLike.amount
    );
    if (!Number.isFinite(amount)) return null;
    const divisor =
      subUnitDivisors && subUnitDivisors[currency] != null ? Number(subUnitDivisors[currency]) : 100;
    return new Decimal(amount).dividedBy(divisor || 100).toNumber();
  } catch (e) {
    return null;
  }
};

const roundMoney = n => {
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};

const listingUuid = listing =>
  listing?.id?.uuid || (typeof listing?.id === 'string' ? listing.id : null);

const txUuid = transaction =>
  transaction?.id?.uuid || (typeof transaction?.id === 'string' ? transaction.id : null);

const publicDataOf = listing => listing?.attributes?.publicData || {};

export const isSecondaryTnListing = listing => {
  if (!listing) return false;
  if (listing.attributes?.publicData) {
    return isTicketNetworkEventListing(listing.attributes.publicData);
  }
  return isTicketNetworkEventListing(listing);
};

const lineItemsOf = transaction =>
  Array.isArray(transaction?.attributes?.lineItems) ? transaction.attributes.lineItems : [];

const lineItemMajorTotal = lineItem => {
  if (!lineItem || lineItem.reversal) return null;
  if (lineItem.lineTotal) return moneyToMajor(lineItem.lineTotal);
  const unit = moneyToMajor(lineItem.unitPrice);
  const qty = Number(lineItem.quantity);
  if (unit != null && Number.isFinite(qty) && qty !== 0) {
    return roundMoney(unit * qty);
  }
  if (lineItem.percentage != null && lineItem.unitPrice) {
    const base = moneyToMajor(lineItem.unitPrice);
    const pct = Number(lineItem.percentage);
    if (base != null && Number.isFinite(pct)) {
      return roundMoney((base * pct) / 100);
    }
  }
  return null;
};

const findLineItem = (transaction, code, { reversal = false } = {}) =>
  lineItemsOf(transaction).find(li => li?.code === code && !!li.reversal === !!reversal) || null;

const sumCustomerFacingReversals = transaction => {
  const codes = [LINE_ITEM_ITEM, LINE_ITEM_CUSTOMER_COMMISSION, LINE_ITEM_TEAK_PROTECTION];
  let total = 0;
  let found = false;
  codes.forEach(code => {
    const li = findLineItem(transaction, code, { reversal: true });
    const maj = li?.lineTotal ? moneyToMajor(li.lineTotal) : null;
    if (maj != null) {
      total += Math.abs(maj);
      found = true;
    }
  });
  return found ? roundMoney(total) : null;
};

/**
 * Marketplace receives = |customer-commission| + |provider-commission|.
 */
export const marketplaceRevenueFromTransaction = transaction => {
  const customer = findLineItem(transaction, LINE_ITEM_CUSTOMER_COMMISSION);
  const provider = findLineItem(transaction, LINE_ITEM_PROVIDER_COMMISSION);
  let total = 0;
  let found = false;
  const c = lineItemMajorTotal(customer);
  if (c != null) {
    total += Math.abs(c);
    found = true;
  }
  const p = lineItemMajorTotal(provider);
  if (p != null) {
    total += Math.abs(p);
    found = true;
  }
  return found ? roundMoney(total) : null;
};

export const feeParamsFromTransaction = transaction => {
  const buyer = lineItemMajorTotal(findLineItem(transaction, LINE_ITEM_CUSTOMER_COMMISSION));
  const protection = lineItemMajorTotal(findLineItem(transaction, LINE_ITEM_TEAK_PROTECTION));
  const payout = moneyToMajor(transaction?.attributes?.payoutTotal);
  const payin = moneyToMajor(transaction?.attributes?.payinTotal);
  const marketplace_revenue = marketplaceRevenueFromTransaction(transaction);
  const out = {};
  if (buyer != null) out.buyer_fee = Math.abs(buyer);
  if (protection != null && protection > 0) out.protection_premium = protection;
  if (payout != null) out.payout_total = payout;
  if (payin != null && payout != null) {
    out.gross_payin_payout_spread = roundMoney(payin - payout);
  }
  if (marketplace_revenue != null) out.marketplace_revenue = marketplace_revenue;
  return out;
};

/**
 * Build reconciled ecommerce items + value (Option A: protection as separate item).
 * Pure — usable from browser and future server Measurement Protocol.
 */
export const buildReconciledEcommerceItems = ({
  transaction,
  listing,
  orderData,
  fallbackUnitPriceMajor,
  fallbackQuantity,
} = {}) => {
  const currency =
    transaction?.attributes?.payinTotal?.currency ||
    listing?.attributes?.price?.currency ||
    'USD';
  const qty =
    Number(orderData?.quantity) > 0
      ? Math.round(Number(orderData.quantity))
      : Number(transaction?.attributes?.protectedData?.tnSelection?.quantity) > 0
        ? Math.round(Number(transaction.attributes.protectedData.tnSelection.quantity))
        : Number(fallbackQuantity) > 0
          ? Math.round(Number(fallbackQuantity))
          : 1;

  const itemLi = findLineItem(transaction, LINE_ITEM_ITEM);
  const commissionLi = findLineItem(transaction, LINE_ITEM_CUSTOMER_COMMISSION);
  const protectionLi = findLineItem(transaction, LINE_ITEM_TEAK_PROTECTION);

  const itemTotal = lineItemMajorTotal(itemLi);
  const commissionTotal = lineItemMajorTotal(commissionLi);
  const protectionTotal = lineItemMajorTotal(protectionLi);

  let ticketBundle =
    itemTotal != null
      ? roundMoney(itemTotal + (commissionTotal != null ? Math.abs(commissionTotal) : 0))
      : null;

  if (ticketBundle == null && fallbackUnitPriceMajor != null) {
    ticketBundle = roundMoney(Number(fallbackUnitPriceMajor) * qty);
  }

  const payinMajor = moneyToMajor(transaction?.attributes?.payinTotal);
  if (ticketBundle == null && payinMajor != null) {
    ticketBundle = roundMoney(payinMajor - (protectionTotal > 0 ? protectionTotal : 0));
  }

  const ticketUnit = ticketBundle != null && qty > 0 ? roundMoney(ticketBundle / qty) : null;

  const title = listing?.attributes?.title || 'Secondary tickets';
  const tn = orderData?.tnSelection || transaction?.attributes?.protectedData?.tnSelection || {};
  const section = tn.section || tn.ticket_section;
  const row = tn.row || tn.ticket_row;
  const variantParts = [section ? `Section ${section}` : null, row ? `Row ${row}` : null].filter(
    Boolean
  );

  const items = [];
  if (ticketUnit != null && qty > 0) {
    items.push({
      item_id: String(listingUuid(listing) || 'secondary_ticket'),
      item_name: String(title),
      affiliation: GA4_AFFILIATION,
      item_category: GA4_ITEM_CATEGORY_TICKETS,
      ...(variantParts.length ? { item_variant: variantParts.join(' / ') } : {}),
      price: ticketUnit,
      quantity: qty,
    });
  }

  if (protectionTotal != null && protectionTotal > 0) {
    items.push({
      item_id: GA4_PROTECTION_ITEM_ID,
      item_name: 'Ticket Protection',
      affiliation: GA4_AFFILIATION,
      item_category: GA4_ITEM_CATEGORY_PROTECTION,
      price: protectionTotal,
      quantity: 1,
    });
  }

  const itemsSum = roundMoney(
    items.reduce((sum, it) => sum + Number(it.price) * Number(it.quantity), 0)
  );

  const value =
    itemsSum != null && items.length
      ? itemsSum
      : payinMajor != null
        ? payinMajor
        : ticketBundle;

  return { currency, value, items, quantity: qty };
};

/**
 * Shared purchase event params for browser gtag + future Measurement Protocol.
 * Does NOT include client_id/session_id (those are MP envelope fields).
 */
export const buildSecondaryPurchaseEventParams = ({ transaction, listing, orderData } = {}) => {
  const id = txUuid(transaction);
  if (!id) return null;
  const ecommerce = buildReconciledEcommerceItems({ transaction, listing, orderData });
  const fees = feeParamsFromTransaction(transaction);
  const tn = orderData?.tnSelection || transaction?.attributes?.protectedData?.tnSelection || {};
  return sanitizeGa4Params({
    transaction_id: String(id),
    currency: ecommerce.currency,
    value: ecommerce.value,
    items: ecommerce.items,
    ...fees,
    ...listingContextParams(listing, {
      sharetribe_transaction_id: String(id),
      ticket_group_id: tn.ticketGroupId ? String(tn.ticketGroupId) : undefined,
      ticket_section: tn.section,
      ticket_row: tn.row,
      ticket_quantity: ecommerce.quantity,
      tn_event_id: tn.eventId || publicDataOf(listing).ticketNetworkEventId,
    }),
  });
};

/**
 * Shape for future GA4 Measurement Protocol purchase (server).
 * client_id / session_id come from persisted transaction metadata.ga4Attribution.
 */
export const buildMeasurementProtocolPurchaseBody = ({
  clientId,
  sessionId,
  transaction,
  listing,
  orderData,
} = {}) => {
  const params = buildSecondaryPurchaseEventParams({ transaction, listing, orderData });
  if (!params || !clientId) return null;
  return {
    client_id: String(clientId),
    events: [
      {
        name: 'purchase',
        params: sanitizeGa4Params({
          ...params,
          ...(sessionId != null && sessionId !== ''
            ? { session_id: String(sessionId) }
            : {}),
        }),
      },
    ],
  };
};

export const listingContextParams = (listing, extras = {}) => {
  const pd = publicDataOf(listing);
  const id = listingUuid(listing);
  return sanitizeGa4Params({
    listing_id: id ? String(id) : undefined,
    tn_event_id: pd.ticketNetworkEventId ? String(pd.ticketNetworkEventId) : undefined,
    event_title: listing?.attributes?.title || undefined,
    event_date: pd.eventDate || pd.startDate || pd.eventStart || undefined,
    venue_name: pd.venueName || pd.venue || undefined,
    event_city: pd.venueCity || pd.city || undefined,
    ...extras,
  });
};

// —— process helpers ——

export const getPurchaseProcessState = transaction => {
  try {
    const processName = resolveLatestProcessName(
      transaction?.attributes?.processName || 'default-purchase'
    );
    const process = getProcess(processName);
    return process.getState(transaction);
  } catch (e) {
    return null;
  }
};

export const hasConfirmedPayment = transaction => {
  const transitions = transaction?.attributes?.transitions;
  const orderPlaced = t =>
    t === purchaseTransitions.CONFIRM_PAYMENT ||
    t === purchaseTransitions.REQUEST_FREE_PURCHASE ||
    t === purchaseTransitions.REQUEST_FREE_PURCHASE_AFTER_INQUIRY;
  if (Array.isArray(transitions)) {
    return transitions.some(t => orderPlaced(t?.transition));
  }
  return orderPlaced(transaction?.attributes?.lastTransition);
};

export const hasCaptureAccept = transaction => {
  const transitions = transaction?.attributes?.transitions;
  if (Array.isArray(transitions)) {
    return transitions.some(t => CAPTURE_TRANSITIONS.has(t?.transition));
  }
  return CAPTURE_TRANSITIONS.has(transaction?.attributes?.lastTransition);
};

export const isEligibleSecondaryPurchase = (transaction, listing) => {
  if (!isSecondaryTnListing(listing)) return false;
  if (!txUuid(transaction)) return false;
  if (!hasConfirmedPayment(transaction)) return false;
  if (!hasCaptureAccept(transaction)) return false;
  const payin = moneyToMajor(transaction?.attributes?.payinTotal);
  if (payin == null || payin <= 0) return false;
  const tn = transaction?.attributes?.protectedData?.tnSelection;
  if (!tn?.ticketGroupId && !tn?.lockRequestId) return false;
  const state = getPurchaseProcessState(transaction);
  if (!state || !POST_CAPTURE_PURCHASE_STATES.has(state)) return false;
  return true;
};

/**
 * Refund only for transition/cancel (process.edn has Stripe full refund).
 */
export const resolveSecondaryRefundValue = transaction => {
  const fromReversals = sumCustomerFacingReversals(transaction);
  if (fromReversals != null && fromReversals > 0) return fromReversals;
  // Full Stripe refund of customer-paid components for transition/cancel
  const ecommerce = buildReconciledEcommerceItems({ transaction });
  if (ecommerce.value != null && ecommerce.value > 0) return ecommerce.value;
  return null;
};

export const isEligibleSecondaryRefund = (transaction, listing) => {
  if (!isSecondaryTnListing(listing)) return false;
  if (!hasConfirmedPayment(transaction)) return false;
  const payin = moneyToMajor(transaction?.attributes?.payinTotal);
  if (payin == null || payin <= 0) return false;
  const last = transaction?.attributes?.lastTransition;
  if (!PAID_STRIPE_REFUND_TRANSITIONS.has(last)) return false;
  const state = getPurchaseProcessState(transaction);
  if (state !== purchaseStates.CANCELED) return false;
  if (resolveSecondaryRefundValue(transaction) == null) return false;
  return true;
};

// —— trackers ——

export const trackSecondaryViewItem = ({ listing, measurementId } = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  const id = listingUuid(listing);
  if (!id) return false;
  const title = listing?.attributes?.title;
  if (!title) return false;

  const dedupeKey = `${VIEW_ITEM_DEDUP_PREFIX}${id}`;
  if (safeSessionGet(dedupeKey) === '1') return false;
  safeSessionSet(dedupeKey, '1');

  const currency = listing?.attributes?.price?.currency || 'USD';
  void measurementId;

  return trackGa4Event(
    'view_item',
    sanitizeGa4Params({
      currency,
      items: [
        {
          item_id: String(id),
          item_name: String(title),
          affiliation: GA4_AFFILIATION,
          item_category: GA4_ITEM_CATEGORY_TICKETS,
        },
      ],
      ...listingContextParams(listing),
    })
  );
};

export const summarizeTicketGroupPrices = ticketGroups => {
  const prices = (ticketGroups || [])
    .map(tg => Number(tg?.tgPrice ?? tg?.tgBasePrice))
    .filter(n => Number.isFinite(n) && n > 0);
  if (!prices.length) {
    return { lowestUnitPrice: undefined, highestUnitPrice: undefined };
  }
  return {
    lowestUnitPrice: Math.min(...prices),
    highestUnitPrice: Math.max(...prices),
  };
};

export const trackSecondaryInventoryLoaded = ({
  listing,
  inventoryCount,
  ticketGroupCount,
  lowestUnitPrice,
  highestUnitPrice,
  currency,
  inventoryLoadDurationMs,
  loadKey,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  const count = Number(ticketGroupCount ?? inventoryCount);
  if (!Number.isFinite(count) || count <= 0) return false;

  const ctx = listingContextParams(listing);
  const dedupeKey = `${INVENTORY_DEDUP_PREFIX}${ctx.listing_id || ''}:${ctx.tn_event_id || ''}:${loadKey ||
    'default'}:loaded`;
  if (safeSessionGet(dedupeKey) === '1') return false;
  safeSessionSet(dedupeKey, '1');

  return trackGa4Event(
    'secondary_inventory_loaded',
    sanitizeGa4Params({
      ...ctx,
      inventory_count: count,
      ticket_group_count: count,
      lowest_unit_price: lowestUnitPrice != null ? Number(lowestUnitPrice) : undefined,
      highest_unit_price: highestUnitPrice != null ? Number(highestUnitPrice) : undefined,
      currency: currency || 'USD',
      inventory_load_duration_ms:
        inventoryLoadDurationMs != null ? Math.round(Number(inventoryLoadDurationMs)) : undefined,
    })
  );
};

export const trackSecondaryInventoryEmpty = ({
  listing,
  currency,
  inventoryLoadDurationMs,
  loadKey,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  const ctx = listingContextParams(listing);
  const dedupeKey = `${INVENTORY_DEDUP_PREFIX}${ctx.listing_id || ''}:${ctx.tn_event_id || ''}:${loadKey ||
    'default'}:empty`;
  if (safeSessionGet(dedupeKey) === '1') return false;
  safeSessionSet(dedupeKey, '1');

  return trackGa4Event(
    'secondary_inventory_empty',
    sanitizeGa4Params({
      ...ctx,
      currency: currency || 'USD',
      inventory_load_duration_ms:
        inventoryLoadDurationMs != null ? Math.round(Number(inventoryLoadDurationMs)) : undefined,
    })
  );
};

/**
 * GA4 recommended ecommerce `select_item` — fires on each explicit Buy Tickets
 * click that successfully opens the TN quantity / pre-checkout drawer.
 * No session dedupe: every click is a meaningful interaction.
 * Must fire before `secondary_ticket_selected` in the funnel.
 */
export const trackSecondarySelectItem = ({
  listing,
  ticketGroupId,
  ticketSection,
  ticketRow,
  ticketUnitPrice,
  currency,
  deliveryMethod,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  const groupId = ticketGroupId != null ? String(ticketGroupId).trim() : '';
  if (!groupId) return false;
  const title = listing?.attributes?.title;
  if (!title) return false;

  const variantParts = [
    ticketSection ? `Section ${ticketSection}` : null,
    ticketRow ? `Row ${ticketRow}` : null,
  ].filter(Boolean);

  return trackGa4Event(
    'select_item',
    sanitizeGa4Params({
      item_list_id: GA4_SECONDARY_ITEM_LIST_ID,
      item_list_name: GA4_SECONDARY_ITEM_LIST_NAME,
      items: [
        {
          item_id: groupId,
          item_name: String(title),
          affiliation: GA4_AFFILIATION,
          item_category: GA4_ITEM_CATEGORY_TICKETS,
          ...(variantParts.length ? { item_variant: variantParts.join(' / ') } : {}),
          ...(ticketUnitPrice != null && Number.isFinite(Number(ticketUnitPrice))
            ? { price: Number(ticketUnitPrice) }
            : {}),
          quantity: 1,
        },
      ],
      ...listingContextParams(listing, {
        ticket_group_id: groupId,
        ticket_section: ticketSection,
        ticket_row: ticketRow,
        currency: currency || 'USD',
        delivery_method: deliveryMethod,
      }),
    })
  );
};

export const trackSecondaryTicketSelected = ({
  listing,
  ticketGroupId,
  ticketSection,
  ticketRow,
  ticketQuantity,
  ticketUnitPrice,
  value,
  currency,
  deliveryMethod,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_ticket_selected',
    sanitizeGa4Params({
      ...listingContextParams(listing, {
        ticket_group_id: ticketGroupId ? String(ticketGroupId) : undefined,
        ticket_section: ticketSection,
        ticket_row: ticketRow,
        ticket_quantity:
          ticketQuantity != null && Number(ticketQuantity) > 0
            ? Math.round(Number(ticketQuantity))
            : undefined,
        ticket_unit_price: ticketUnitPrice != null ? Number(ticketUnitPrice) : undefined,
        value: value != null ? Number(value) : undefined,
        currency: currency || 'USD',
        delivery_method: deliveryMethod,
      }),
    })
  );
};

export const trackSecondaryBeginCheckout = ({
  listing,
  transaction,
  orderData,
  sharetribeTransactionId,
  dedupeKey,
  fallbackUnitPriceMajor,
  measurementId,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  const tn = orderData?.tnSelection || {};
  const key =
    dedupeKey ||
    sharetribeTransactionId ||
    transaction?.id?.uuid ||
    `${listingUuid(listing)}:${tn.lockRequestId || tn.ticketGroupId || 'checkout'}`;
  const storageKey = `${BEGIN_CHECKOUT_DEDUP_PREFIX}${key}`;
  if (safeSessionGet(storageKey) === '1') return false;
  safeSessionSet(storageKey, '1');

  // Capture GA ids for later metadata persist (async capture / MP).
  getGa4ClientAndSessionIds(measurementId)
    .then(ids => cacheGa4AttributionIds(ids))
    .catch(() => {});

  const txId = sharetribeTransactionId || txUuid(transaction);
  if (txId) {
    persistGa4AttributionForTransaction(txId, measurementId).catch(() => {});
  }

  const ecommerce = buildReconciledEcommerceItems({
    transaction,
    listing,
    orderData,
    fallbackUnitPriceMajor: fallbackUnitPriceMajor ?? tn.unitPrice,
    fallbackQuantity: orderData?.quantity || tn.quantity,
  });

  return trackGa4Event(
    'begin_checkout',
    sanitizeGa4Params({
      currency: ecommerce.currency,
      value: ecommerce.value,
      items: ecommerce.items,
      ...listingContextParams(listing, {
        sharetribe_transaction_id: txId ? String(txId) : undefined,
        ticket_group_id: tn.ticketGroupId ? String(tn.ticketGroupId) : undefined,
        ticket_section: tn.section,
        ticket_row: tn.row,
        ticket_quantity: ecommerce.quantity,
        tn_event_id: tn.eventId || publicDataOf(listing).ticketNetworkEventId,
      }),
    })
  );
};

export const trackSecondaryPurchase = ({ listing, transaction, orderData } = {}) => {
  if (!isEligibleSecondaryPurchase(transaction, listing)) return false;
  const id = txUuid(transaction);
  if (hasLocalDedupe(PURCHASE_DEDUP_PREFIX, id)) return false;

  const params = buildSecondaryPurchaseEventParams({ transaction, listing, orderData });
  if (!params || params.value == null) return false;

  const sent = trackGa4Event('purchase', params);
  if (sent) markLocalDedupe(PURCHASE_DEDUP_PREFIX, id);
  return sent;
};

/**
 * Classify lock/checkout-prep failures for GA4 funnel debugging (no PII).
 * Used when Buy Tickets fails before begin_checkout.
 */
export const classifySecondaryLockFailureReason = err => {
  if (!err) return 'unknown';
  if (err.cancelled === true) return 'requote_declined';
  const code = err.data?.code || err.code || null;
  const status = err.status != null ? Number(err.status) : null;
  const msg = String(err.message || err.data?.error || err.data?.message || '');
  if (code === 'unavailable' || /no tickets available|no longer available/i.test(msg)) {
    return 'unavailable';
  }
  if (code === 'requote_required') return 'requote_required';
  if (
    err.isTraffic === true ||
    /throttl|traffic|quota|temporarily unavailable|Message throttled/i.test(msg)
  ) {
    return 'traffic';
  }
  if (/timeout|timed out|aborted/i.test(msg)) return 'timeout';
  if (Number.isFinite(status) && status > 0) return `http_${status}`;
  return 'error';
};

/** Lock succeeded and shopper is navigating to CheckoutPage (pairs with begin_checkout). */
export const trackSecondaryLockSucceeded = ({
  listing,
  lockRequestId,
  ticketGroupId,
  quantity,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_lock_succeeded',
    listingContextParams(listing, {
      lock_request_id: lockRequestId ? String(lockRequestId) : undefined,
      ticket_group_id: ticketGroupId ? String(ticketGroupId) : undefined,
      ticket_quantity:
        quantity != null && Number(quantity) > 0 ? Math.round(Number(quantity)) : undefined,
    })
  );
};

/** Lock / pre-checkout failure — shopper never reaches begin_checkout. */
export const trackSecondaryLockFailed = ({
  listing,
  reason,
  status,
  code,
  ticketGroupId,
} = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_lock_failed',
    listingContextParams(listing, {
      failure_reason: reason || 'unknown',
      http_status: status != null && Number.isFinite(Number(status)) ? Number(status) : undefined,
      error_code: code ? String(code) : undefined,
      ticket_group_id: ticketGroupId ? String(ticketGroupId) : undefined,
    })
  );
};

/** Confirm-and-pay clicked (before guest signup / initiate / Stripe). */
export const trackSecondaryCheckoutPayClicked = ({ listing, isGuest } = {}) => {
  if (!isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_checkout_pay_clicked',
    listingContextParams(listing, {
      is_guest: isGuest === true,
    })
  );
};

/** Guest Flex signup or PromoTix sync failed — Pay aborted. */
export const trackSecondaryGuestCheckoutFailed = ({ listing, reason } = {}) => {
  if (listing && !isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_guest_checkout_failed',
    sanitizeGa4Params({
      failure_reason: reason || 'unknown',
      ...(listing ? listingContextParams(listing) : {}),
    })
  );
};

/** Sharetribe initiate / speculate payment failed. */
export const trackSecondaryInitiateFailed = ({ listing, reason, status } = {}) => {
  if (listing && !isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_initiate_failed',
    sanitizeGa4Params({
      failure_reason: reason || 'unknown',
      http_status: status != null && Number.isFinite(Number(status)) ? Number(status) : undefined,
      ...(listing ? listingContextParams(listing) : {}),
    })
  );
};

/** Stripe confirm / payment step failed after initiate. */
export const trackSecondaryPaymentFailed = ({ listing, reason } = {}) => {
  if (listing && !isSecondaryTnListing(listing)) return false;
  return trackGa4Event(
    'secondary_payment_failed',
    sanitizeGa4Params({
      failure_reason: reason || 'unknown',
      ...(listing ? listingContextParams(listing) : {}),
    })
  );
};

export const trackSecondaryRefund = ({ listing, transaction } = {}) => {
  if (!isEligibleSecondaryRefund(transaction, listing)) return false;
  const id = txUuid(transaction);
  if (!id) return false;
  if (hasLocalDedupe(REFUND_DEDUP_PREFIX, id)) return false;

  const value = resolveSecondaryRefundValue(transaction);
  if (value == null) return false;
  const ecommerce = buildReconciledEcommerceItems({ transaction, listing });
  const fees = feeParamsFromTransaction(transaction);

  const sent = trackGa4Event(
    'refund',
    sanitizeGa4Params({
      transaction_id: String(id),
      currency: ecommerce.currency,
      value,
      items: ecommerce.items,
      ...fees,
      ...listingContextParams(listing, {
        sharetribe_transaction_id: String(id),
      }),
    })
  );
  if (sent) markLocalDedupe(REFUND_DEDUP_PREFIX, id);
  return sent;
};
